import { Editor } from "@tinymce/tinymce-react";
import { twMerge } from "tailwind-merge";

import { TextEditorProps } from "./TextEditor.types";
import { HelperText, Label } from "../../Display";
import { Theme } from "./TextEditor.theme";
import { truncateHtmlToTextLength } from "./TextEditor.utils";

const pluginsConfig = [
  'advlist autolink lists link image charmap print preview anchor',
  'searchreplace visualblocks code fullscreen',
  'insertdatetime media table paste code help wordcount'
];

const toolbarConfig = [
  'undo redo',
  'formatselect',
  'bold italic backcolor',
  'alignleft aligncenter alignright alignjustify',
  'more_tools',
].join(' | ');

const toolbarGroupsConfig = {
  more_tools: {
    icon: 'more-drawer',
    tooltip: 'More tools',
    items: [
      'bullist numlist outdent indent',
      'removeformat',
      'code',
      'link',
      'help',
    ].join(' | '),
  },
};

export function TextEditor({
  initialValue,
  placeholder,
  helperText,
  className,
  onChange,
  required,
  value,
  label,
  error,
  disabled,
  ref,
  height = 280,
  onInit,
  maxLength,
  toolbar = toolbarConfig,
  toolbarGroups = toolbarGroupsConfig,
}: TextEditorProps) {
  return (
    <div className={twMerge("relative", className)}>
      {(!!label) && <Label {...{ required, error }}>{label}</Label>}
      <div
        className={twMerge(error && Theme.inputError, Theme.input)}
        ref={ref}
      >
        <Editor
          disabled={disabled}
          onEditorChange={(content, editor) => {
            if (maxLength != null && editor) {
              const textLength = editor.getContent({ format: 'text' }).length;
              if (textLength > maxLength) {
                const truncated = truncateHtmlToTextLength(content, maxLength);
                editor.setContent(truncated);
                onChange?.(truncated);
                return;
              }
            }
            onChange?.(content);
          }}
          onInit={onInit}
          init={{
            placeholder,
            height: height,
            menubar: false,
            plugins: pluginsConfig,
            toolbar: toolbar,
            toolbar_mode: 'floating',
            toolbar_groups: toolbarGroups,
            content_style: 'body { font-family:Manrope, sans-serif; font-size:14px } body::before { color: #98A2B3 !important; }',
            setup: (editor) => {
              if (maxLength != null) {
                editor.on('keydown', (e) => {
                  const allowedKeys = [
                    'Backspace',
                    'Delete',
                    'ArrowLeft',
                    'ArrowRight',
                    'ArrowUp',
                    'ArrowDown',
                    'Tab',
                    'Escape',
                    'Home',
                    'End',
                  ];
                  if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
                    return;
                  }
                  const text = editor.getContent({ format: 'text' });
                  const selection = editor.selection.getContent({ format: 'text' });
                  const remaining = maxLength - (text.length - (selection ? selection.length : 0));
                  if (remaining <= 0) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                });

                editor.on('paste', (e) => {
                  const currentText = editor.getContent({ format: 'text' });
                  const selection = editor.selection.getContent({ format: 'text' });
                  const remaining = maxLength - (currentText.length - (selection ? selection.length : 0));
                  if (remaining <= 0) {
                    e.preventDefault();
                  }
                });

                editor.on('PastePreProcess', (e) => {
                  const currentText = editor.getContent({ format: 'text' });
                  const selection = editor.selection.getContent({ format: 'text' });
                  const remaining = maxLength - (currentText.length - (selection ? selection.length : 0));
                  if (remaining <= 0) {
                    e.preventDefault();
                    e.content = '';
                    return;
                  }
                  e.content = truncateHtmlToTextLength(e.content, remaining);
                });
              }
            },
          }}
          tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
          {...{
            initialValue,
            value
          }}
        />
      </div>
      {(!!helperText) && <HelperText {...{ error }}>{helperText}</HelperText>}

    </div>
  );
}